import {useDisclosure} from '@mantine/hooks';
import type React from 'react';
import {FaCircleUser} from 'react-icons/fa6';
import {MdIosShare, MdMoreHoriz, MdOutlineChatBubble} from 'react-icons/md';
import {COLOR_BLACK_2} from '../../../../config/constants/colorCodes';
import {COMMUNITY} from '../../../../config/constants/routes';
import type {CommunityTopic} from '../../../../core/entities/community/schema';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {convertToLink} from '../../../../shared/services/convertToLink';
import {formatDateFullWithTime} from '../../../../shared/services/formatDate';
import {ReportTopicModal} from './Presenter/ReportTopicModal';
import {ShareButtonMenu} from './Presenter/ShareButtonMenu';
import {TopicItemMoreMenu} from './Presenter/TopicItemMoreMenu';
import {TopicFollowButton} from './TopicFollowButton';

export const TopicDetail: React.FC<{
  topic: CommunityTopic;
  isSignedIn: boolean;
}> = ({topic, isSignedIn}) => {
  const [isShareMenuOpened, {open: openShareMenu, close: closeShareMenu}] =
    useDisclosure(false);
  const [isMoreMenuOpened, {open: openMoreMenu, close: closeMoreMenu}] =
    useDisclosure(false);
  const [
    isReportModalOpened,
    {open: openReportModal, close: closeReportModal},
  ] = useDisclosure(false);
  const [
    isNoLoginModalOpened,
    {open: openNoLoginModal, close: closeNoLoginModal},
  ] = useDisclosure(false);
  const formattedCreatedAt = formatDateFullWithTime(topic.createdAt);
  const formattedLastPostedAt = formatDateFullWithTime(topic.lastPostedAt);

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="flex justify-start items-center">
          <FaCircleUser size={16} fill={COLOR_BLACK_2} />
          <p className="text-sm text-black-1 font-bold leading ml-4">
            {topic.nickname}
          </p>
          {formattedCreatedAt && (
            <p className="text-sm text-black-1 leading ml-8">
              投稿日: {formattedCreatedAt}
            </p>
          )}
        </div>
        {formattedLastPostedAt && (
          <p className="text-sm text-black-1 leading">
            {formattedLastPostedAt} 更新
          </p>
        )}
      </div>
      <p className="text-md text-black-1 font-bold leading line-clamp-2 mt-12">
        {topic.title}
      </p>
      {topic.tagNames.length > 0 && (
        <div className="flex flex-wrap items-center mt-8">
          {topic.tagNames.map((tag) => (
            <div
              key={tag}
              className="bg-black-5 rounded-full p-4 mr-4 min-w-[59px] pt-[2px] px-8"
            >
              <p className="text-sm text-black-1 font-bold leading text-center">
                {tag}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-hidden mt-12">
        <p className={'text-base text-black-1 leading '}>
          {convertToLink(topic.body)}
        </p>
      </div>
      <div className="flex justify-end items-center mt-12">
        <div className="relative">
          <button
            type="button"
            className="hover:opacity-80"
            onClick={() => {
              if (!isSignedIn) {
                openNoLoginModal();
                return;
              }
              isMoreMenuOpened ? closeMoreMenu() : openMoreMenu();
            }}
          >
            <MdMoreHoriz size={24} color={COLOR_BLACK_2} />
          </button>
          <TopicItemMoreMenu
            opened={isMoreMenuOpened}
            close={closeMoreMenu}
            openReportModal={openReportModal}
          />
        </div>
        <div className="ml-16 relative">
          <button
            type="button"
            className="hover:opacity-80"
            onClick={() => {
              if (!isSignedIn) {
                openNoLoginModal();
                return;
              }
              isShareMenuOpened ? closeShareMenu() : openShareMenu();
            }}
          >
            <MdIosShare size={24} color={COLOR_BLACK_2} />
          </button>
          <ShareButtonMenu
            opened={isShareMenuOpened}
            close={closeShareMenu}
            currentUrl={`${COMMUNITY}/${topic.id}`}
            title={topic.title}
          />
        </div>
        <div className="ml-16">
          <div className="border-[2px] border-black-2 border-collapse rounded-full flex items-center py-4 px-12 w-[80px]">
            <div className="mr-4">
              <MdOutlineChatBubble size={18} color={COLOR_BLACK_2} />
            </div>
            <p className="text-sm text-black-2 font-bold leading w-[calc(100%-22px)] text-center">
              {topic.commentCount}
            </p>
          </div>
        </div>
        <div className="ml-4">
          <TopicFollowButton
            topicId={topic.id}
            isFollowedInitial={topic.followed}
            followCountInitial={topic.followerCount}
            isSignedIn={isSignedIn}
          />
        </div>
      </div>
      <ReportTopicModal
        opened={isReportModalOpened}
        open={openReportModal}
        onClose={closeReportModal}
        topic={topic}
      />
      <NoLoginAlertModal
        opened={isNoLoginModalOpened}
        onClose={closeNoLoginModal}
      />
    </>
  );
};
